"use client";

// ============================================================
// ANSICHTSSACHE-AGENT – Avatar Component
// ============================================================
// Seit dem 07.10.2026 nennt jeder Kreis beim Ueberfahren nach
// 120 ms den vollen Namen (`useNamensHinweis`) — bei gleichen
// Initialen sonst nicht zu unterscheiden. `hint` ersetzt den Text
// („Name · Ersteller“), `hint={false}` schaltet ihn ab (Farbmuster
// im Farbwaehler).
// ============================================================

import type { MouseEvent } from "react";
import { Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import { getInitials } from "@/lib/utils";
import { avatarColorClasses, avatarColorStyle } from "@/lib/person-colors";
import { useNamensHinweis } from "./namens-hinweis";

interface AvatarProps {
  firstName: string;
  lastName: string;
  imageUrl?: string | null;
  /** Die gewaehlte Farbe der Person (`User.color`, `#rrggbb`); leer = Gegenakzent. */
  color?: string | null;
  size?: "sm" | "md" | "lg";
  /** Text des Namens-Hinweises; Vorgabe ist der volle Name, `false` = keiner. */
  hint?: string | false;
  /**
   * Der Kreis steht in einer Listenzeile mit Zeilenflaeche
   * (`Zeilenverweis`/`ZEILENFLAECHE`). Ohne das laege er unter der
   * Flaeche und bekaeme den Zeiger nie — der Hinweis bliebe aus. Ein
   * Klick auf den Kreis geht dann an die Flaeche weiter, die Zeile
   * oeffnet wie ueberall sonst.
   */
  inRow?: boolean;
  className?: string;
}

const sizeClasses = {
  sm: "h-7 w-7 text-caption",
  md: "h-9 w-9 text-body-sm",
  lg: "h-12 w-12 text-body-md",
};

/** Klick an das weiterreichen, was ohne den gehobenen Kreis darunter laege. */
function anDieZeileWeiter(e: MouseEvent<HTMLElement>) {
  const kreis = e.currentTarget;
  kreis.style.pointerEvents = "none";
  const darunter = document.elementFromPoint(e.clientX, e.clientY);
  kreis.style.pointerEvents = "";
  // Ein Vorfahr bekommt den Klick ohnehin (Karte, Knopf).
  if (!(darunter instanceof HTMLElement) || darunter.contains(kreis)) return;
  e.stopPropagation();
  e.preventDefault();
  darunter.dispatchEvent(
    new globalThis.MouseEvent("click", {
      bubbles: true,
      cancelable: true,
      ctrlKey: e.ctrlKey,
      metaKey: e.metaKey,
      shiftKey: e.shiftKey,
      altKey: e.altKey,
    }),
  );
}

export function Avatar({
  firstName,
  lastName,
  imageUrl,
  color,
  size = "md",
  hint,
  inRow = false,
  className,
}: AvatarProps) {
  const name = `${firstName} ${lastName}`.trim();
  const { ausloeser, blase } = useNamensHinweis(hint === false ? null : (hint ?? name));
  const zeile = inRow ? { onClick: anDieZeileWeiter } : {};

  if (imageUrl) {
    return (
      <>
        {/* eslint-disable-next-line @next/next/no-img-element -- frei hochgeladene Adresse, fuer next/image nicht freigegeben */}
        <img
          src={imageUrl}
          alt={name}
          {...ausloeser}
          {...zeile}
          className={cn(
            "rounded-full object-cover",
            inRow && "relative z-[1]",
            sizeClasses[size],
            className,
          )}
        />
        {blase}
      </>
    );
  }

  return (
    <>
      <div
        role="img"
        aria-label={name}
        {...ausloeser}
        {...zeile}
        // Ohne gewaehlte Farbe Petrol statt Rot. Ein Gesicht
        // BEZEICHNET jemanden, es hebt ihn nicht hervor — und wenn
        // jedes Kuerzel im Hausrot steht, bleibt fuer „ueberfaellig"
        // keine Farbe uebrig. Die Farben und die Vorgabe stehen in
        // `lib/person-colors.ts`.
        className={cn(
          "flex items-center justify-center rounded-full font-medium",
          inRow && "relative z-[1]",
          avatarColorClasses(color),
          sizeClasses[size],
          className,
        )}
        style={avatarColorStyle(color)}
      >
        <span aria-hidden>{getInitials(firstName, lastName)}</span>
      </div>
      {blase}
    </>
  );
}

interface RemovableAvatarProps {
  firstName: string;
  lastName: string;
  color?: string | null;
  /** Text des Namens-Hinweises, Vorgabe der volle Name. */
  hint?: string;
  /** Die Tat des Knopfes, „… abziehen“; ohne `onRemove` steht nur der Kreis. */
  removeLabel?: string;
  onRemove?: (() => void) | null;
  disabled?: boolean;
  className?: string;
}

/**
 * Ein Kreis, den man beim Ueberfahren entfernen kann (Zustaendige
 * einer Aufgabe, Mitglieder eines Teams): der gestrichelte
 * Minus-Knopf legt sich darueber. Der Namens-Hinweis haengt deshalb
 * an der Huelle, nicht am Kreis — unter dem Knopf kaeme er nie an.
 */
export function RemovableAvatar({
  firstName,
  lastName,
  color,
  hint,
  removeLabel,
  onRemove,
  disabled,
  className,
}: RemovableAvatarProps) {
  const name = hint ?? `${firstName} ${lastName}`.trim();
  const { ausloeser, blase } = useNamensHinweis(onRemove && removeLabel ? removeLabel : name);
  return (
    <div {...ausloeser} className="group/kreis relative">
      <Avatar
        firstName={firstName}
        lastName={lastName}
        color={color}
        size="sm"
        hint={false}
        className={cn(
          "duration-fast text-[10px] transition-opacity",
          onRemove && "group-hover/kreis:opacity-0",
          className,
        )}
      />
      {onRemove && (
        <button
          type="button"
          disabled={disabled}
          onClick={onRemove}
          aria-label={removeLabel ?? name}
          className="border-border-strong bg-bg-secondary text-text-tertiary duration-fast hover:text-text-primary absolute inset-0 grid place-items-center rounded-full border border-dashed opacity-0 transition-opacity group-hover/kreis:opacity-100"
        >
          <Minus className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
      {blase}
    </div>
  );
}
